// Getting pictures off the clipboard, and working out where they land on a
// canvas slide.
//
// Copying a picture is how most slide content actually arrives: a diagram out
// of an existing deck, a figure from a web page, a screenshot of a chart. The
// clipboard is the shortest path from "I can see it" to "it is on my slide",
// and it is the one PowerPoint users reach for first.
//
// Kept as its own module, free of React and of the DOM where it can be, so the
// awkward parts — what counts as an image, how a picture is sized to a slide
// without distorting it — can be tested directly rather than through a paste
// event on a canvas.

/**
 * How much of the slide a single pasted picture may occupy before it is
 * scaled down. Half the slide leaves room to drop it beside a heading or a
 * text box without immediately having to resize it.
 */
const MAX_WIDTH_PERCENT = 50;
const MAX_HEIGHT_PERCENT = 50;

/**
 * How far each picture in a multi-image paste is offset from the one before,
 * in percent of the slide. PowerPoint cascades a multi-paste rather than
 * stacking it, because three pictures landing on exactly the same spot look
 * like one picture and the two underneath have to be found by dragging.
 */
const CASCADE_STEP_PERCENT = 4;

/**
 * The image files on a clipboard payload.
 *
 * Two routes, because browsers disagree. `clipboardData.files` is the modern
 * one and covers Chrome and Edge copying out of PowerPoint or a web page.
 * `items` is the fallback: some engines expose a copied picture only as an
 * item whose `getAsFile()` produces the blob. Reading both and de-duplicating
 * costs nothing and is the difference between the feature working in a given
 * browser and silently doing nothing.
 *
 * Anything that is not an image is ignored, so copying a slide out of
 * PowerPoint — which puts text, HTML and a picture on the clipboard at once —
 * pastes as the picture rather than failing on the text.
 */
export const imageFilesFromClipboard = (clipboardData) => {
    if (!clipboardData) return [];

    const found = [];
    const seen = new Set();
    const add = (file) => {
        if (!file || !/^image\//i.test(file.type || '')) return;
        // A file appearing in both `files` and `items` is one picture, not two.
        const key = `${file.name}|${file.size}|${file.type}`;
        if (seen.has(key)) return;
        seen.add(key);
        found.push(file);
    };

    Array.from(clipboardData.files || []).forEach(add);
    Array.from(clipboardData.items || []).forEach((item) => {
        if (item?.kind !== 'file') return;
        try { add(item.getAsFile()); } catch { /* item already consumed */ }
    });

    return found;
};

/**
 * A readable name for a clipboard picture.
 *
 * A copied image arrives as an unnamed "image.png", and that name becomes the
 * default alt text and the caption. Left alone, every pasted picture in a deck
 * would carry the same one, which is worse than useless for a screen reader.
 *
 * Mirrors the naming the image modal already applies to pastes made inside it,
 * so a picture pasted onto the slide and the same picture pasted into the
 * modal end up with the same name.
 */
export const nameClipboardImage = (file, index) => {
    const extension = (String(file.type || '').split('/')[1] || 'png').replace('jpeg', 'jpg');
    try {
        return new File([file], `Pasted image ${index}.${extension}`, { type: file.type });
    } catch {
        return file;   // File constructor unavailable — keep the original
    }
};

/**
 * Where a pasted picture sits on the slide, as a canvas frame in percent.
 *
 * The picture is fitted inside half the slide without distorting it, then
 * centred and cascaded by its position in the paste.
 *
 * Aspect ratio needs the stage's real pixel size because a frame's width and
 * height are percentages of *different* dimensions: 40% wide and 40% tall is
 * not square on a 16:9 slide. Converting through the stage size is what keeps
 * a pasted square square.
 *
 * @param {object}  options
 * @param {number}  options.aspect       naturalWidth / naturalHeight
 * @param {number} [options.index]       position within a multi-image paste
 * @param {number}  options.stageWidth   stage width in px
 * @param {number}  options.stageHeight  stage height in px
 */
export const frameForPastedImage = ({ aspect, index = 0, stageWidth, stageHeight }) => {
    const safeAspect = Number.isFinite(aspect) && aspect > 0 ? aspect : 4 / 3;
    // A percent of width and a percent of height describe different numbers of
    // pixels; this is the factor between them.
    const stageRatio = (stageWidth || 1) / (stageHeight || 1);

    let width = MAX_WIDTH_PERCENT;
    let height = (width * stageRatio) / safeAspect;
    if (height > MAX_HEIGHT_PERCENT) {
        height = MAX_HEIGHT_PERCENT;
        width = (height * safeAspect) / stageRatio;
    }

    // Cascade from centred. Clamped so a long paste cannot walk a picture off
    // the bottom-right corner and out of reach.
    const offset = index * CASCADE_STEP_PERCENT;
    const x = Math.min((100 - width) / 2 + offset, Math.max(0, 100 - width));
    const y = Math.min((100 - height) / 2 + offset, Math.max(0, 100 - height));

    return {
        x: Math.round(Math.max(0, x) * 100) / 100,
        y: Math.round(Math.max(0, y) * 100) / 100,
        w: Math.round(width * 100) / 100,
        h: Math.round(height * 100) / 100
    };
};

/**
 * Read a file into a data URL.
 *
 * Every picture in a deck is stored inline as a data URL — decks travel as a
 * single JSON file, so a reference to a local path would arrive broken on
 * anyone else's machine.
 */
export const fileToDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error || new Error('Could not read the pasted image.'));
    reader.readAsDataURL(file);
});

/** Longest wait for a picture to report its size before it is placed anyway. */
const MEASURE_TIMEOUT_MS = 5000;

/**
 * The natural size of a data-URL image.
 *
 * Falls back to 4:3 rather than rejecting: a picture the browser cannot
 * measure should still land on the slide at a sane size, where it can be
 * resized by hand, instead of the paste failing outright.
 *
 * The timeout matters more than it looks. A malformed data URL can leave an
 * <img> that fires neither `load` nor `error`, and without a deadline this
 * promise would simply never settle — the paste would hang with nothing on the
 * slide and no error to explain it.
 */
export const measureImage = (dataUrl) => new Promise((resolve) => {
    const fallback = { width: 4, height: 3 };
    if (typeof Image === 'undefined') { resolve(fallback); return; }

    let settled = false;
    const finish = (size) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(size);
    };
    const timer = setTimeout(() => finish(fallback), MEASURE_TIMEOUT_MS);

    const image = new Image();
    image.onload = () => finish({
        width: image.naturalWidth || image.width || fallback.width,
        height: image.naturalHeight || image.height || fallback.height
    });
    image.onerror = () => finish(fallback);
    image.src = dataUrl;
});
